import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cloudApi from '../services/cloudApi';
import { viewAs } from '../services/viewAs';
import { saveLastLoginInfo } from '../utils/lastLoginInfo';
import analytics from '../services/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Pick the active workspace given the loaded list and the user's preferences.
 * Priority: localStorage (active session) > server-stored default > first owner workspace > first.
 */
const pickActiveWorkspace = (workspaces, userDefaultId) => {
  if (!workspaces || workspaces.length === 0) return null;
  const localId = cloudApi.getCurrentWorkspaceId();
  return (
    (localId && workspaces.find(w => w.id === localId)) ||
    (userDefaultId && workspaces.find(w => w.id === userDefaultId)) ||
    workspaces.find(w => w.role === 'owner') ||
    workspaces[0]
  );
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [credits, setCredits] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isApiConnected, setIsApiConnected] = useState(true);
  const [viewAsUser, setViewAsUser] = useState(null);
  // Bumped whenever switchWorkspace fires successfully — drives the
  // "Switched to {workspace}" confirmation toast. Hydrated from sessionStorage
  // so the event survives the workspace-switch page reload in DashboardView.
  const [lastSwitchEvent, setLastSwitchEventState] = useState(() => {
    try {
      const raw = sessionStorage.getItem('workspaceSwitchEvent');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const setLastSwitchEvent = (event) => {
    setLastSwitchEventState(event);
    try {
      if (event) sessionStorage.setItem('workspaceSwitchEvent', JSON.stringify(event));
      else sessionStorage.removeItem('workspaceSwitchEvent');
    } catch { /* storage may be unavailable */ }
  };

  useEffect(() => {
    // Initialize and validate existing auth token
    const initializeAuth = async () => {
      try {
        await cloudApi.initAuth();

        if (cloudApi.isAuthenticated()) {
          // Validate token by fetching current user.
          // /auth/me is excluded from view-as on the backend, so this always returns the admin.
          try {
            const response = await cloudApi.request('/auth/me');
            setUser(response.user);
            setIsAuthenticated(true);

            // Returning user (existing token) — re-identify so events aren't anonymous.
            analytics.identifyUser(response.user);

            // Restore view-as UI state and workspace list if active before refresh/new-tab
            const viewAsUserData = viewAs.restore();
            if (viewAsUserData) {
              setViewAsUser(viewAsUserData);
            }

            try {
              // Pass viewAsUserId per-request so concurrent requests don't pick up the header
              const wsOptions = viewAsUserData ? { viewAsUserId: viewAsUserData.id } : undefined;
              const userWorkspaces = await cloudApi.getWorkspaces(wsOptions);
              setWorkspaces(userWorkspaces);
              const currentWs = pickActiveWorkspace(userWorkspaces, response.user?.default_workspace_id);
              if (currentWs) await cloudApi.setWorkspaceId(currentWs.id);
              setCurrentWorkspace(currentWs || null);
            } catch (wsError) {
              console.error('Failed to load workspaces:', wsError);
            }
          } catch (error) {
            console.error('Failed to validate auth token:', error);

            if (error.message === 'Authentication required. Please log in again.') {
              viewAs.clear();
              await cloudApi.clearAuthToken();
              setIsAuthenticated(false);
              setUser(null);
              setIsApiConnected(true);
            } else {
              console.warn('Auth validation failed due to network error, keeping user logged in');
              setIsAuthenticated(true);
              setIsApiConnected(false);
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Notify React when a 401 clears the token (e.g. during polling)
    cloudApi.onAuthError = () => {
      viewAs.clear();
      setIsAuthenticated(false);
      setUser(null);
      setViewAsUser(null);
      setSubscription(null);
      setCredits(null);
      setSubscriptionLoading(true);
      // A silent 401 logout should also drop the PostHog identity.
      analytics.reset();
    };
    return () => { cloudApi.onAuthError = null; };
  }, []);

  const hydrateSession = useCallback(async (response, { method } = {}) => {
    setUser(response.user);
    setIsAuthenticated(true);

    // Analytics — identify the REAL user (never effectiveUser), then track
    // signup vs signin based on the backend's userCreated flag.
    if (response.userCreated) {
      analytics.trackSignUp(response.user, { method });
    } else {
      analytics.trackSignIn(response.user, { method });
    }

    try {
      const userWorkspaces = await cloudApi.getWorkspaces();
      setWorkspaces(userWorkspaces);
      const currentWs = pickActiveWorkspace(userWorkspaces, response.user?.default_workspace_id);
      if (currentWs) await cloudApi.setWorkspaceId(currentWs.id);
      setCurrentWorkspace(currentWs || null);
    } catch (wsError) {
      console.error('Failed to load workspaces:', wsError);
    }
  }, []);

  const login = async (email, password, options = {}) => {
    try {
      setError(null);
      const response = await cloudApi.login(email, password, options);

      await hydrateSession(response, { method: 'password' });

      return { success: true, user: response.user };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const sendMagicLink = useCallback(async (email, options = {}) => {
    try {
      setError(null);
      await cloudApi.requestMagicLink(email, options);
      // Save last login info when magic link is sent
      saveLastLoginInfo('email', email);
      return { success: true };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  const completeMagicLogin = useCallback(async (token, options = {}) => {
    try {
      setError(null);
      const response = await cloudApi.verifyMagicLink(token, options);

      await hydrateSession(response, { method: 'magic_link' });

      // Update last login info with the actual user email after successful login
      if (response.user?.email) {
        saveLastLoginInfo('email', response.user.email);
      }

      return { success: true, user: response.user };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [hydrateSession]);

  const completeLoginCode = useCallback(async (email, code, options = {}) => {
    try {
      setError(null);
      const response = await cloudApi.verifyLoginCode(email, code, options);

      await hydrateSession(response, { method: 'login_code' });

      if (response.user?.email) {
        saveLastLoginInfo('email', response.user.email);
      }

      return { success: true, user: response.user };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [hydrateSession]);

  const register = async (email, password, name, options = {}) => {
    try {
      setError(null);
      const response = await cloudApi.register(email, password, name, options);

      await hydrateSession(response, { method: 'password' });

      return { success: true, user: response.user };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const loginWithGoogle = useCallback(async (code, options = {}) => {
    try {
      setError(null);
      const response = await cloudApi.loginWithGoogle(code, options);
      await hydrateSession(response, { method: 'google' });

      // Save last login info for Google sign-in
      if (response.user?.email) {
        saveLastLoginInfo('google', response.user.email);
      }

      return { success: true, user: response.user };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [hydrateSession]);

  const logout = async () => {
    try {
      // Call backend logout endpoint
      await cloudApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if backend call fails
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setSubscription(null);
      setCredits(null);
      setSubscriptionLoading(true);
      // Clear PostHog identity so the next login on this browser starts clean.
      analytics.reset();
    }
  };

  const switchWorkspace = async (workspaceId) => {
    try {
      await cloudApi.switchWorkspace(workspaceId);
      const workspace = workspaces.find(w => w.id === workspaceId);
      setCurrentWorkspace(workspace);
      if (workspace) {
        // Re-point analytics before billing refetches so events don't keep the prior plan.
        analytics.setWorkspaceGroup(workspace);
        setLastSwitchEvent({ id: workspace.id, name: workspace.name, ts: Date.now() });
      }
      // Best-effort persist of the user's preferred default workspace
      cloudApi.setDefaultWorkspace(workspaceId).catch((err) => {
        console.warn('Failed to persist default workspace:', err);
      });
      return workspace;
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      throw error;
    }
  };

  const dismissSwitchToast = () => setLastSwitchEvent(null);

  const refreshWorkspaces = async () => {
    try {
      const userWorkspaces = await cloudApi.getWorkspaces();
      setWorkspaces(userWorkspaces);
      const currentWs = pickActiveWorkspace(userWorkspaces, user?.default_workspace_id);
      if (currentWs) await cloudApi.setWorkspaceId(currentWs.id);
      setCurrentWorkspace(currentWs || null);
      return userWorkspaces;
    } catch (error) {
      console.error('Failed to refresh workspaces:', error);
      throw error;
    }
  };

  const refreshSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const [billingData, creditsData] = await Promise.all([
        cloudApi.getBillingStatus(),
        cloudApi.getCreditsBalance(),
      ]);
      setSubscription(billingData);
      setCredits(creditsData?.balance ?? null);
      analytics.setWorkspaceGroup(currentWorkspace, billingData, creditsData?.balance);
      return { subscription: billingData, credits: creditsData?.balance ?? null };
    } catch (error) {
      // Swallow so a transient failure leaves prior state intact and banners just don't update.
      console.error('Failed to refresh subscription:', error);
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (isAuthenticated && currentWorkspace) {
      refreshSubscription();
    } else if (isAuthenticated && !currentWorkspace) {
      // Authenticated but no workspace — nothing to fetch, but don't leave gates hanging.
      setSubscriptionLoading(false);
    }
  }, [isAuthenticated, currentWorkspace, refreshSubscription]);

  const enterViewAs = async (targetUser) => {
    const { workspaces: targetWorkspaces, currentWorkspace: firstWs } = await viewAs.enter(targetUser);
    setViewAsUser(targetUser);
    setWorkspaces(targetWorkspaces);
    setCurrentWorkspace(firstWs);
  };

  const exitViewAs = async () => {
    await viewAs.exit();
    setViewAsUser(null);
    await refreshWorkspaces();
  };

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const retryConnection = async () => {
    try {
      // Try to fetch current user to test connection
      const response = await cloudApi.request('/auth/me');
      setUser(response.user);
      setIsAuthenticated(true);
      setIsApiConnected(true);
      analytics.identifyUser(response.user);

      // Reload workspaces
      try {
        const userWorkspaces = await cloudApi.getWorkspaces();
        setWorkspaces(userWorkspaces);
        const currentWs = pickActiveWorkspace(userWorkspaces, response.user?.default_workspace_id);
        if (currentWs) await cloudApi.setWorkspaceId(currentWs.id);
        setCurrentWorkspace(currentWs || null);
      } catch (wsError) {
        console.error('Failed to load workspaces:', wsError);
      }

      return true;
    } catch (error) {
      if (error.message === 'Authentication required. Please log in again.') {
        // Auth failed, but API is reachable
        await cloudApi.clearAuthToken();
        setIsAuthenticated(false);
        setUser(null);
        setIsApiConnected(true);
      } else {
        // Still can't connect
        setIsApiConnected(false);
      }
      return false;
    }
  };

  // When in view-as mode, expose the target user's identity for display purposes.
  // Auth-critical fields (isAuthenticated, isAdmin) always reflect the real admin.
  const effectiveUser = viewAsUser ? { ...user, ...viewAsUser } : user;

  const value = {
    isAuthenticated,
    user,
    effectiveUser,
    loading,
    error,
    login,
    register,
    logout,
    workspaces,
    currentWorkspace,
    subscription,
    credits,
    subscriptionLoading,
    refreshSubscription,
    switchWorkspace,
    refreshWorkspaces,
    lastSwitchEvent,
    dismissSwitchToast,
    isApiConnected,
    retryConnection,
    isAdmin: user?.role === 'admin',
    viewAsUser,
    enterViewAs,
    exitViewAs,
    updateUser,
    sendMagicLink,
    completeMagicLogin,
    completeLoginCode,
    loginWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
